/**
 * PDF 导出服务
 * 使用 html2pdf.js 库将 DOM 元素导出为 PDF
 */

import html2pdf from 'html2pdf.js'
import { ExportOptions } from '../../../types/export'

/**
 * 导出为 PDF（从 DOM 元素）
 */
export const exportToPDF = async (
  element: HTMLElement,
  options: ExportOptions
): Promise<void> => {
  if (!element) {
    throw new Error('导出元素不能为空')
  }

  try {
    const scale = window.devicePixelRatio || 2

    const margin = options.margin || {
      top: 10,
      right: 10,
      bottom: 10,
      left: 10
    }

    const pageSize = options.pageSize || 'A4'
    const orientation = options.orientation || 'portrait'

    const opt = {
      margin: [margin.top, margin.right, margin.bottom, margin.left] as [number, number, number, number],
      filename: options.filename || 'document.pdf',
      image: {
        type: 'jpeg' as const,
        quality: 0.98
      },
      html2canvas: {
        scale,
        useCORS: true,
        logging: false,
        allowTaint: true,
        scrollY: 0,
        scrollX: 0,
        backgroundColor: '#ffffff',
        ignoreElements: (el: HTMLElement) => {
          return el.classList.contains('vditor-preview__action')
        }
      },
      jsPDF: {
        unit: 'mm',
        format: pageSize.toLowerCase() as 'a4' | 'a3' | 'letter',
        orientation: orientation as 'portrait' | 'landscape'
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    }

    console.log('PDF导出配置:', opt)

    await html2pdf()
      .set(opt)
      .from(element)
      .save()

  } catch (error) {
    console.error('PDF 导出失败:', error)
    throw new Error(`PDF 导出失败: ${error instanceof Error ? error.message : '未知错误'}`)
  }
}

/**
 * 预览 PDF（返回 blob URL）
 */
export const previewPDF = async (
  element: HTMLElement,
  options: ExportOptions
): Promise<string> => {
  if (!element) {
    throw new Error('导出元素不能为空')
  }

  try {
    const scale = window.devicePixelRatio || 2

    const margin = options.margin || {
      top: 10,
      right: 10,
      bottom: 10,
      left: 10
    }

    const pageSize = options.pageSize || 'A4'
    const orientation = options.orientation || 'portrait'

    const opt = {
      margin: [margin.top, margin.right, margin.bottom, margin.left] as [number, number, number, number],
      image: {
        type: 'jpeg' as const,
        quality: 0.98
      },
      html2canvas: {
        scale,
        useCORS: true,
        logging: false,
        allowTaint: true,
        scrollY: 0,
        scrollX: 0,
        backgroundColor: '#ffffff',
        ignoreElements: (el: HTMLElement) => {
          return el.classList.contains('vditor-preview__action')
        }
      },
      jsPDF: {
        unit: 'mm',
        format: pageSize.toLowerCase() as 'a4' | 'a3' | 'letter',
        orientation: orientation as 'portrait' | 'landscape'
      }
    }

    const pdfBlob = await html2pdf()
      .set(opt)
      .from(element)
      .outputPdf('blob')

    return URL.createObjectURL(pdfBlob)
  } catch (error) {
    console.error('PDF 预览失败:', error)
    throw new Error(`PDF 预览失败: ${error instanceof Error ? error.message : '未知错误'}`)
  }
}

/**
 * 检查 PDF 导出支持
 */
export const checkPDFSupport = (): boolean => {
  return typeof window !== 'undefined' && 
         typeof document !== 'undefined' && 
         typeof Blob !== 'undefined'
}